import { useState } from 'react';
import { Eye, Loader2, ChevronRight } from 'lucide-react';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { PdfZoomViewer } from './PdfZoomViewer';

interface ContractPreviewProps {
  /** Object URL (blob) del PDF contratto non firmato. */
  pdfUrl?: string | null;
  loading?: boolean;
}

/**
 * Anteprima del contratto in cima alla pagina di firma. Pattern Notion-like:
 * una card-trigger che apre il PDF in un drawer dal basso (mobile) o in un
 * modale centrale (desktop) — stessa UX dell'anteprima email nelle statistiche
 * newsletter. Il rendering responsive è delegato a ResponsiveModal.
 */
export function ContractPreview({ pdfUrl, loading }: ContractPreviewProps) {
  const [open, setOpen] = useState(false);
  const ready = !!pdfUrl && !loading;

  return (
    <>
      <button
        type="button"
        onClick={() => ready && setOpen(true)}
        disabled={!ready}
        className="w-full rounded-xl border border-black/[0.08] bg-white px-4 py-3.5 flex items-center justify-between gap-3 text-left shadow-sm transition-colors hover:bg-black/[0.02] disabled:cursor-default disabled:hover:bg-white"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F7F6F3] shrink-0">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#787774]" />
            ) : (
              <Eye className="w-4 h-4 text-[#787774]" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-[#37352F]">Anteprima contratto</span>
            <span className="block text-xs text-[#787774] truncate">
              {loading
                ? 'Generazione anteprima del contratto…'
                : pdfUrl
                  ? 'Tocca per visualizzare il documento prima di firmare'
                  : 'Anteprima non disponibile'}
            </span>
          </span>
        </span>
        {ready && <ChevronRight className="w-4 h-4 text-[#787774] shrink-0" />}
      </button>

      {/* Drawer fullscreen (web e mobile) con viewer pdf.js: pagine a larghezza
          piena, zoom +/− e scroll nativo — l'iframe PDF non scrolla su iOS. */}
      <ResponsiveModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Anteprima contratto"
        size="fullscreen"
        mobileHeight="full"
        noPadding
      >
        {pdfUrl ? (
          <div className="h-[calc(100vh-9rem)] md:h-full">
            <PdfZoomViewer pdfUrl={pdfUrl} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-16 text-sm text-[#787774]">
            Anteprima non disponibile.
          </div>
        )}
      </ResponsiveModal>
    </>
  );
}
